/**
 * PageShell — the platform frame (spec Decision 1, "Structure"; v1.2 change 01): the release ribbon
 * (Keylime, Bog text, 28px, not sticky) ABOVE everything, then the L0 bar (Bog / #061D21 dark, 56px,
 * the platform's wordmark 24px, the platform entries, search filling the bar, the tools), L1 bar (44px: [App ▾] · Data · up to 4 app menus · Requests · Help), breadcrumb (12.5px
 * meta), then the content region: full width between the gutters, no max-width anywhere, the
 * body never scrolls sideways. The shell owns the gutter; pages own only their content.
 * The pieces are slots so the app shell (src/app/shell) keeps its data and routing.
 */
import * as React from 'react';
import { FeedbackPill } from './feedback';

export interface PageShellProps {
  l0: React.ReactNode;
  ribbon?: React.ReactNode;
  l1?: React.ReactNode;
  crumb?: React.ReactNode;
  children: React.ReactNode;
  /** Rendered after the content (command palettes, panels). */
  overlays?: React.ReactNode;
  mainRef?: React.Ref<HTMLElement>;
  columnRef?: React.Ref<HTMLDivElement>;
}

export function PageShell({ l0, ribbon, l1, crumb, children, overlays, mainRef, columnRef }: PageShellProps) {
  useL0Bottom();
  return (
    <div className="k-shell" data-kit="shell" ref={columnRef}>
      {/* v1.2 change 01: the release stage is the first thing on every page; L0 sticks once it scrolls away */}
      {ribbon}
      {l0}
      {l1}
      {crumb}
      <main className="k-content k-gutter" data-kit="content" ref={mainRef}>
        {children}
      </main>
      {overlays}
    </div>
  );
}

/**
 * `--k-l0-bottom`: where the L0 bar ends in the window right now (ribbon + bar at the top of the page, the bar
 * alone once the ribbon has scrolled away), so the docked pane starts under the bar and never covers it.
 */
function useL0Bottom() {
  React.useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    const update = () => {
      frame = 0;
      const bar = document.querySelector('.k-shell > .k-l0');
      if (bar) root.style.setProperty('--k-l0-bottom', `${Math.max(0, Math.round(bar.getBoundingClientRect().bottom))}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // QA16-A-1: the ribbon mounts once /api/meta answers and moves the bar, but on a full-height page the body's size
    // does not change with it — so measure again when the shell's bands change (a band mounts or resizes) and when
    // a pane opens (`data-pane` on <html>), not only on the body's size
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    ro?.observe(document.body);
    const shell = document.querySelector('.k-shell');
    const watchBands = () => {
      for (const el of Array.from(shell?.children ?? [])) if (!el.matches('main')) ro?.observe(el);
    };
    watchBands();
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => {
      watchBands();
      schedule();
    });
    if (shell) mo?.observe(shell, { childList: true });
    mo?.observe(root, { attributes: true, attributeFilter: ['data-pane'] });
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro?.disconnect();
      mo?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      root.style.removeProperty('--k-l0-bottom');
    };
  }, []);
}

export function L0Bar({ mark, nav, search, right, ...rest }: { mark: React.ReactNode; nav: React.ReactNode; search?: React.ReactNode; right: React.ReactNode; 'data-testid'?: string }) {
  return (
    <header className="k-l0" data-kit="l0" data-testid={rest['data-testid'] ?? 'platform-bar'}>
      <div className="k-l0-in k-gutter">
        {mark}
        {nav}
        {search}
        <div className="k-l0-right">{right}</div>
      </div>
    </header>
  );
}

/** The release-stage ribbon: part of the page, never dismissible. */
export function Ribbon({ stage, children, srText }: { stage: string; children: React.ReactNode; srText?: string }) {
  return (
    <div role="status" className="k-ribbon k-gutter" data-kit="ribbon" data-testid="release-ribbon">
      <p style={{ margin: 0 }}>
        <b>{stage}</b>
        {srText ? <span className="sr-only">{srText}</span> : null}
        <span aria-hidden> · </span>
        {children}
      </p>
    </div>
  );
}

export function L1Bar({ label, app, lead, tail, ...rest }: { label: string; app?: string; lead: React.ReactNode; tail: React.ReactNode; 'data-testid'?: string }) {
  return (
    <nav aria-label={label} className="k-l1" data-kit="l1" data-testid={rest['data-testid'] ?? 'app-menu-bar'} data-app={app}>
      <div className="k-l1-in k-gutter">
        {lead}
        <div className="k-l1-std">{tail}</div>
      </div>
    </nav>
  );
}

export function Breadcrumb({ children }: { children: React.ReactNode }) {
  return (
    <p className="k-crumb k-gutter" data-kit="crumb" data-testid="app-crumb">
      {children}
    </p>
  );
}

/** The page head: eyebrow, 26px title, one-line description, actions on the right. */
export function PageHead({ eyebrow, title, description, actions, ...rest }: { eyebrow?: React.ReactNode; title: React.ReactNode; description?: React.ReactNode; actions?: React.ReactNode; 'data-testid'?: string }) {
  return (
    <header className="k-page-head" data-kit="page-head" data-testid={rest['data-testid'] ?? 'page-head'}>
      <div className="k-page-head-text">
        {eyebrow ? <span className="k-eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {/* R-15 D2 (T-111): the feedback pill on every page head */}
      <div className="k-page-head-actions">
        {actions}
        <FeedbackPill />
      </div>
    </header>
  );
}

/** True below 768px (the shell's phone layout), kept current as the window resizes. */
export function usePhoneWidth(): boolean {
  const query = '(max-width: 767px)';
  const [phone, setPhone] = React.useState(() => typeof globalThis.matchMedia !== 'undefined' && matchMedia(query).matches);
  React.useEffect(() => {
    if (typeof globalThis.matchMedia === 'undefined') return;
    const mql = matchMedia(query);
    const on = () => setPhone(mql.matches);
    on();
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return phone;
}

/** True on a Mac or an iPad/iPhone keyboard, where the palette's shortcut is ⌘K. */
export function isMacPlatform(nav: Pick<Navigator, 'platform' | 'userAgent'> | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  return /Mac|iPhone|iPad|iPod/i.test(`${nav?.platform ?? ''} ${nav?.userAgent ?? ''}`);
}

/**
 * The L0 bar's search box (v1.2 change 01): it fills the space between the platform entries and the tools
 * (240–640px), reads "Search <platform name>", shows the palette's shortcut at its right end (hidden below
 * 1100px) and becomes an icon button below 768px. It opens the command palette, the one Ctrl K / ⌘K opens.
 */
export function L0Search({ label, onOpen, 'data-testid': testId }: { label: string; onOpen: () => void; 'data-testid'?: string }) {
  const shortcut = isMacPlatform() ? '⌘K' : 'Ctrl K';
  return (
    <button type="button" data-kit="search" className="k-l0-search" data-testid={testId} onClick={onOpen} aria-label={label} aria-keyshortcuts={isMacPlatform() ? 'Meta+K' : 'Control+K'}>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <span className="k-l0-search-text" aria-hidden>
        {label}
      </span>
      <kbd className="k-l0-search-key" aria-hidden data-testid={testId ? `${testId}-shortcut` : undefined}>
        {shortcut}
      </kbd>
    </button>
  );
}
